/**
 *  React Context for application state management.
 * @module app
 */

import type { AppContextValue } from "../types";

import { createContext } from "react";

/**
 * React Context that provides application state to the component tree.
 *
 * @type {React.Context<AppContextValue | null>}
 * @module app
 * @category Context
 *
 * @remarks
 * This context is created with a default value of `null` and must be used
 * within an {@link AppProvider} component. Components can access the context
 * value using the {@link useApp} hook.
 *
 * The context provides the entire {@link AppState} to consuming components,
 * allowing them to read application configuration, authentication status,
 * and UI state.
 *
 * @example
 * ```typescript
 * // Using the context directly (not recommended)
 * import { useContext } from 'react';
 * import { AppContext } from './Application.context';
 *
 * const context = useContext(AppContext);
 * if (!context) throw new Error('Must be used within AppProvider');
 *
 * // Preferred: use the useApp hook
 * import { useApp } from './Application.provider';
 * const state = useApp();
 * ```
 *
 * @see {@link AppProvider} for the context provider component
 * @see {@link useApp} for the recommended way to access context
 * @see {@link AppContextValue} for the shape of the context value
 */
export const AppContext = createContext<AppContextValue | null>(null);
